import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "item", // each sortable row/item
    "handle", // drag handle inside an item
    "positionInput", // position input inside an item
    "destroyInput", // _destroy input inside an item
    "links", // container with add links to act as anchor for insertBefore
  ];

  connect() {
    this.dragged = null;
    this.linksEl = this.hasLinksTarget ? this.linksTarget : null;

    this.initItems();
    this.updatePositions();

    this.observer = new MutationObserver(() => {
      this.linksEl = this.hasLinksTarget ? this.linksTarget : null;
      this.initItems();
      this.updatePositions();
    });
    this.observer.observe(this.element, {
      childList: false,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
  }

  initItems() {
    this.items.forEach((item) => {
      // Ensure the entire item is NOT draggable
      item.removeAttribute("draggable");
      item.removeEventListener("dragstart", this.onDragStart);
      item.removeEventListener("dragend", this.onDragEnd);

      const handle = item.querySelector(this.targetSelector("handle"));
      if (!handle) return;
      if (handle.dataset.sortableInitialized === "true") return;

      handle.setAttribute("draggable", "true");
      handle.addEventListener("dragstart", this.onDragStart);
      handle.addEventListener("dragend", this.onDragEnd);
      handle.dataset.sortableInitialized = "true";
    });

    if (!this.element.dataset.sortableContainerInitialized) {
      this.element.addEventListener("dragover", this.onDragOver);
      this.element.addEventListener("drop", this.onDrop);
      this.element.dataset.sortableContainerInitialized = "true";
    }
  }

  get items() {
    return this.itemTargets || [];
  }

  onDragStart = (e) => {
    const handle = e.currentTarget;
    const item = handle.closest(this.targetSelector("item"));
    if (!item) return;
    this.dragged = item;
    this.dragged.classList.add("dragging");
    const collapse = item.querySelector(".accordion-collapse");
    if (collapse) collapse.classList.remove("show");
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  };

  onDragEnd = (e) => {
    if (this.dragged) {
      this.dragged.classList.remove("dragging");
      this.dragged = null;
    }
    this.updatePositions();
  };

  onDragOver = (e) => {
    if (!this.dragged) return;
    e.preventDefault();
    const afterElement = this.getDragAfterElement(e.clientY);
    if (afterElement == null) {
      const anchor = this.linksEl;
      if (anchor) {
        if (anchor.previousElementSibling !== this.dragged) {
          this.element.insertBefore(this.dragged, anchor);
        }
      } else if (this.element.lastElementChild !== this.dragged) {
        this.element.appendChild(this.dragged);
      }
    } else if (afterElement !== this.dragged) {
      this.element.insertBefore(this.dragged, afterElement);
    }
  };

  onDrop = (e) => {
    e.preventDefault();
    this.updatePositions();
  };

  getDragAfterElement(y) {
    const candidates = this.items.filter((el) => el !== this.dragged);
    let closest = { offset: Number.NEGATIVE_INFINITY, element: null };
    candidates.forEach((child) => {
      const box = child.getBoundingClientRect();
      const offset = y - (box.top + box.height / 2);
      if (offset < 0 && offset > closest.offset) {
        closest = { offset, element: child };
      }
    });
    return closest.element;
  }

  updatePositions() {
    let index = 1;
    this.items.forEach((item) => {
      const destroyInput = item.querySelector(
        this.targetSelector("destroyInput"),
      );
      const isDestroyed = destroyInput && destroyInput.value === "1";
      const isHidden = item.style && item.style.display === "none";
      if (isDestroyed || isHidden) return;

      const positionInput = item.querySelector(
        this.targetSelector("positionInput"),
      );
      if (positionInput) {
        positionInput.value = index++;
        positionInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  // Build a selector for a Stimulus target for this controller, e.g.
  // [data-forms--sortable-target="item"]
  targetSelector(name) {
    return `[data-${this.identifier}-target="${name}"]`;
  }
}
